import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import {
  Home as HomeIcon,
  AccountTree as TreeIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { systemConfigService } from '../../services/systemConfigService';
import { NavigationStyle } from '../../types/admin';
import { NavigationVariant } from './NavigationVariants';

/**
 * NavigationMenu Component
 *
 * Main navigation menu with route links
 *
 * Features:
 * - Home, Tree, People, Admin links
 * - Active route highlighting
 * - Role-based visibility (Admin link for admins only)
 * - Responsive (hidden on mobile, replaced by drawer)
 *
 * @component
 * @param {NavigationMenuProps} props - Component props
 * @returns {JSX.Element} Navigation menu
 */
interface NavigationMenuProps {
  isMobile?: boolean;
  onNavigate?: () => void; // Callback to close mobile drawer
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  requiresAdmin?: boolean;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  isMobile = false,
  onNavigate,
}) => {
  const [navigationStyle, setNavigationStyle] = useState<NavigationStyle>('current');
  const [loading, setLoading] = useState(true);

  // Load navigation style from global settings
  useEffect(() => {
    const loadNavigationStyle = async () => {
      try {
        const config = await systemConfigService.getConfig();
        if (config && config.lookAndFeel) {
          setNavigationStyle(config.lookAndFeel.navigationStyle);
        }
      } catch (error) {
        console.error('Error loading navigation style:', error);
        // Use default on error
        setNavigationStyle('current');
      } finally {
        setLoading(false);
      }
    };

    loadNavigationStyle();
  }, []);

  // Show loading state briefly
  if (loading) {
    return null;
  }

  // Use the NavigationVariant component with the configured style
  return (
    <NavigationVariant
      variant={navigationStyle}
      isMobile={isMobile}
      onNavigate={onNavigate}
      demoMode={false}
    />
  );
};
